/*
 * <founder-videos>: click a card to play its video with sound and controls.
 * Uploaded videos play in place; YouTube / Vimeo links load their iframe on click.
 * Only one video plays at a time.
 * With data-autoplay="true", videos also run as a muted loop while on screen
 * (browsers only allow muted autoplay); a click then restarts with sound.
 */
if (!customElements.get('founder-videos')) {
  customElements.define(
    'founder-videos',
    class FounderVideos extends HTMLElement {
      connectedCallback() {
        this.frames = Array.from(this.querySelectorAll('[data-fv-frame]'));

        this.frames.forEach((frame) => {
          const button = frame.querySelector('[data-fv-play]');
          if (!button) return;
          button.addEventListener('click', () => this.play(frame));

          const video = frame.querySelector('video');
          if (video) {
            video.addEventListener('ended', () => this.reset(frame));
          }
        });

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (this.dataset.autoplay === 'true' && !reduceMotion && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.preview(entry.target);
                } else {
                  this.pausePreview(entry.target);
                }
              });
            },
            { threshold: 0.4 }
          );
          this.frames.forEach((frame) => this.observer.observe(frame));
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      /* ---------- Silent looping preview ---------- */
      preview(frame) {
        // A video the visitor started with sound is left alone
        if (frame.classList.contains('is-playing')) return;

        const video = frame.querySelector('video');
        if (video) {
          video.muted = true;
          video.loop = true;
          video.controls = false;
          video.preload = 'auto';
          const attempt = video.play();
          if (attempt) attempt.then(() => frame.classList.add('is-preview')).catch(() => {});
          return;
        }

        // YouTube / Vimeo: load a muted autoplaying player (it has its own sound button)
        const embed = frame.dataset.embed;
        if (embed && !frame.querySelector('iframe')) {
          const separator = embed.includes('?') ? '&' : '?';
          this.addIframe(frame, `${embed}${separator}mute=1&muted=1&loop=1`);
          frame.classList.add('is-playing');
        }
      }

      pausePreview(frame) {
        if (!frame.classList.contains('is-preview')) return;
        const video = frame.querySelector('video');
        if (video && !video.paused) video.pause();
      }

      /* ---------- Play with sound ---------- */
      play(frame) {
        this.frames.forEach((other) => {
          if (other !== frame) this.stop(other);
        });

        frame.classList.remove('is-preview');
        frame.classList.add('is-playing');
        const video = frame.querySelector('video');

        if (video) {
          video.loop = false;
          video.controls = true;
          video.preload = 'auto';
          video.muted = false;
          video.currentTime = 0;
          const attempt = video.play();
          if (attempt) attempt.catch(() => {});
          return;
        }

        const embed = frame.dataset.embed;
        if (embed && !frame.querySelector('iframe')) this.addIframe(frame, embed);
      }

      addIframe(frame, src) {
        const iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.className = 'fv__iframe';
        iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.title = frame.querySelector('[data-fv-play]')?.getAttribute('aria-label') || 'Video';
        frame.querySelector('.fv__media').appendChild(iframe);
      }

      stop(frame) {
        const video = frame.querySelector('video');
        if (video && frame.classList.contains('is-playing')) {
          // Back to the silent loop rather than freezing mid-sentence
          this.reset(frame);
          return;
        }
        if (video && !video.paused && !frame.classList.contains('is-preview')) video.pause();
        const iframe = frame.querySelector('iframe');
        if (iframe && this.dataset.autoplay !== 'true') {
          iframe.remove();
          frame.classList.remove('is-playing');
        }
      }

      reset(frame) {
        const video = frame.querySelector('video');
        if (video) {
          video.controls = false;
          video.currentTime = 0;
        }
        frame.classList.remove('is-playing');
        if (this.observer && video) this.preview(frame);
      }
    }
  );
}
